"use client"

import React, { ReactNode, useState } from "react"
import * as Tooltip from "@radix-ui/react-tooltip"
import clsx from "clsx"
import { Box } from "ui/components/atoms/_atoms"
import Icon from "ui/components/atoms/icon"
import { Label, P } from "ui/components/atoms/typography"
import { BaseStyles } from "ui/types/base-styles"
import { generateStylesFromProps } from "ui/utils/styles"

import styles from "./Tag.module.css"

export type TagProps = BaseStyles & {
  label?: string | ReactNode
  sublabel?: string
  icon?: string
  tooltip?: string
  $type?: "primary" | "secondary"
  $border?: boolean
  $iconColor?: string
  $borderColor?: string
  $color?: string
  $showBorder?: boolean
  $iconSize?: string
}

const Tag = ({
  label,
  sublabel,
  icon,
  tooltip,
  $type = "primary",
  $color,
  $iconColor,
  $iconSize = "L",
  $borderColor,
  $showBorder = true,
  className = "",
  style = {},
  ...rest
}: TagProps & { className?: string; style?: React.CSSProperties }) => {
  const [openPopover, setOpenPopover] = useState(false)
  const generatedStyles = generateStylesFromProps(rest)

  const handlePopoverEnter = () => {
    setOpenPopover(true)
  }

  const handlePopoverLeave = () => {
    setOpenPopover(false)
  }

  const wrapperClasses = clsx(
    styles.wrapper,
    {
      [styles.secondary]: $type === "secondary",
      [styles.withBorder]: $type === "secondary" && $showBorder,
      [styles.noLabel]: !label,
      [styles.withIcon]: icon || tooltip,
    },
    className,
  )

  const wrapperStyles = {
    ...generatedStyles,
    border:
      $type === "secondary" && $showBorder && $borderColor
        ? `1px solid var(--color-${$borderColor})`
        : undefined,
    ...style,
  }

  return (
    <div className={wrapperClasses} style={wrapperStyles}>
      {icon && <Icon icon={icon} $color={$iconColor ?? $color} $size={$iconSize} />}
      {(label || sublabel) && (
        <Box $width="100%" style={{ flex: "1" }}>
          {sublabel && (
            <Label $type="third" $color={$iconColor} $marginBottom="XXS">
              {sublabel}
            </Label>
          )}
          {typeof label === "string" || typeof label === "number" ? (
            <Label $type="secondary" $color={$color}>
              {label}
            </Label>
          ) : (
            <>{label}</>
          )}
        </Box>
      )}
      {tooltip && (
        <>
          <Tooltip.Provider>
            <Tooltip.Root open={openPopover}>
              <Tooltip.Trigger
                asChild
                onMouseEnter={handlePopoverEnter}
                onMouseLeave={handlePopoverLeave}
              >
                <button className={styles.tooltipButton}>
                  <Icon icon="info-circle" $size="M" />
                </button>
              </Tooltip.Trigger>

              <Tooltip.Portal>
                <Tooltip.Content
                  sideOffset={0}
                  onMouseEnter={handlePopoverEnter}
                  onMouseLeave={handlePopoverLeave}
                  style={{ zIndex: 999 }}
                  collisionPadding={10}
                >
                  <Tooltip.Arrow style={{ fill: "white", zIndex: 999 }} width={10} height={5} />
                  <div className={styles.tooltipWrapper}>
                    <P $small $marginBottom="0" $color="second">
                      {tooltip}
                    </P>
                  </div>
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </>
      )}
    </div>
  )
}

export default Tag
